/**
 * The visual layer for run evidence.
 *
 * Design notes worth keeping:
 * - Confidence is *ordinal*, so it gets a single-hue ramp (dark = more
 *   confident in light mode, light = more confident in dark mode), not
 *   categorical hues. Both ramps were validated against their own surface.
 * - Coverage is one series, so there is no legend — the title names it.
 * - Every value that colour encodes is also written as text, so nothing
 *   depends on hue alone.
 * - Segments carry a 2px surface gap so adjacent fills read as separate marks.
 */

import { humanise } from "../lib/format";
import type { Confidence } from "../lib/types";

/* --------------------------------------------------------- confidence mix */

const CONFIDENCE_ORDER: Confidence[] = ["high", "medium", "low"];
const CONFIDENCE_FILL: Record<Confidence, string> = {
  high: "bg-conf-high",
  medium: "bg-conf-medium",
  low: "bg-conf-low",
};

export function ConfidenceBar({
  mix,
  definitions,
}: {
  mix: Record<string, number>;
  definitions?: Record<string, string>;
}) {
  const total = CONFIDENCE_ORDER.reduce((sum, key) => sum + (mix[key] ?? 0), 0);

  if (total === 0) {
    return <p className="px-4 py-6 text-sm text-muted">No findings recorded yet.</p>;
  }

  return (
    <div className="px-4 py-4">
      <div className="flex h-3 w-full gap-0.5 overflow-hidden rounded-sm" role="img"
           aria-label={CONFIDENCE_ORDER.map((k) => `${mix[k] ?? 0} ${k}`).join(", ")}>
        {CONFIDENCE_ORDER.map((key) => {
          const count = mix[key] ?? 0;
          if (count === 0) return null;
          return (
            <div
              key={key}
              className={`${CONFIDENCE_FILL[key]} first:rounded-l-sm last:rounded-r-sm`}
              style={{ width: `${(count / total) * 100}%` }}
            />
          );
        })}
      </div>

      <ul className="mt-3 space-y-2">
        {CONFIDENCE_ORDER.map((key) => {
          const count = mix[key] ?? 0;
          return (
            <li key={key} className="flex items-baseline gap-2 text-sm">
              <span
                className={`mt-1 size-2.5 shrink-0 rounded-sm ${CONFIDENCE_FILL[key]}`}
                aria-hidden
              />
              <span className="w-16 font-medium capitalize">{key}</span>
              <span className="tabular w-10 text-right text-ink-2">{count}</span>
              <span className="tabular w-12 text-right text-muted">
                {total ? `${Math.round((count / total) * 100)}%` : "—"}
              </span>
              {definitions?.[key] && (
                <span className="ml-1 hidden flex-1 text-xs text-muted lg:block">
                  {definitions[key]}
                </span>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/* ------------------------------------------------------- coverage by area */

export function CoverageBars({ coverage }: { coverage: Record<string, number> }) {
  const entries = Object.entries(coverage);
  const max = Math.max(1, ...entries.map(([, count]) => count));

  if (entries.length === 0) {
    return <p className="px-4 py-6 text-sm text-muted">No workstreams.</p>;
  }

  return (
    <div className="space-y-2.5 px-4 py-4">
      {entries.map(([key, count]) => (
        <div key={key} className="grid grid-cols-[9.5rem_1fr_2rem] items-center gap-3">
          <span className="truncate text-sm text-ink-2" title={humanise(key)}>
            {humanise(key)}
          </span>
          <div className="h-2.5 w-full rounded-sm bg-surface-2">
            {count > 0 && (
              <div
                className="h-full rounded-sm bg-accent"
                style={{ width: `${Math.max(4, (count / max) * 100)}%` }}
              />
            )}
          </div>
          <span
            className={`tabular text-right text-sm ${
              count === 0 ? "text-muted" : "text-ink"
            }`}
          >
            {count}
          </span>
        </div>
      ))}
      <p className="pt-1 text-xs text-muted">
        Findings recorded per workstream. A zero is either an immaterial
        workstream or a hole in the evidence.
      </p>
    </div>
  );
}
